<template>
	<div
		ref="zoomContainerRef"
		role="button"
		tabindex="0"
		aria-label="Desativar zoom da imagem"
		class="zoomable-image-container relative size-full focus:outline-none focus-visible:ring-2 focus-visible:ring-primary focus-visible:ring-inset"
		@click="handleZoomClick"
		@keydown="handleZoomKeydown"
		@mousemove="handleMouseMove"
		@touchcancel="resetTouchGesture"
		@touchend="handleZoomTouchEnd"
		@touchmove.prevent="handleZoomTouchMove"
		@touchstart="handleZoomTouchStart"
	>
		<img
			ref="imageRef"
			v-bind="imageProps"
			class="size-full object-contain object-center transition-transform duration-200"
			@load="onImageLoad"
		/>
	</div>
</template>

<script setup lang="ts">
import type { ZoomableImageProps } from '.';
import type { ImageViewer as ImageViewerInstance } from 'iv-viewer';

// iv-viewer CSS is included in app/assets/css/main.css to avoid render-blocking CSS
// iv-viewer is loaded dynamically to reduce main bundle size

const props = defineProps<ZoomableImageProps>();

const emits = defineEmits<{
	(event: 'close'): void;
}>();

//Utilizando a imagem com a tag <img> devido a problemas de compatibilidade do NuxtImg com o iv-viewer
const imageRef = ref<HTMLImageElement | null>(null);
const zoomContainerRef = ref<HTMLElement | null>(null);

let viewer: ImageViewerInstance | null = null;
let viewerContainer: HTMLElement | null = null;
let zoomFrame: number | null = null;
let panFrame: number | null = null;
let pendingPointerPosition: { x: number; y: number } | null = null;
let isUnmounted = false;
let maximumZoomPercent = 100;
let touchStartPosition: { x: number; y: number } | null = null;
let touchMoved = false;

const TAP_MOVE_TOLERANCE = 10;

interface ImageLoadedPayload {
	container: HTMLElement;
	instance: ImageViewerInstance;
}

interface ImageViewerOptionsWithListeners {
	listeners: {
		onImageLoaded: (payload: ImageLoadedPayload) => void;
	};
	maxZoom: number;
	snapView: boolean;
	zoomOnMouseWheel: boolean;
}

type ImageViewerConstructor = new (
	element: Element | null,
	options: ImageViewerOptionsWithListeners
) => ImageViewerInstance;

function clampPercentage(value: number): number {
	return Math.min(100, Math.max(0, value));
}

function isDesktopViewport(): boolean {
	return typeof window.matchMedia === 'function'
		? window.matchMedia('(min-width: 768px)').matches
		: window.innerWidth >= 768;
}

function getMaximumZoomPercent(image: HTMLImageElement): number {
	const { naturalHeight, naturalWidth } = image;
	const { height: containerHeight, width: containerWidth } =
		image.getBoundingClientRect();

	if (
		naturalHeight <= 0 ||
		naturalWidth <= 0 ||
		containerHeight <= 0 ||
		containerWidth <= 0
	) {
		return 100;
	}

	const imageRatio = naturalWidth / naturalHeight;
	const containerRatio = containerWidth / containerHeight;
	const renderedWidth =
		imageRatio > containerRatio ? containerWidth : containerHeight * imageRatio;
	const renderedHeight = renderedWidth / imageRatio;
	const naturalSizeZoomPercent = Math.floor(
		Math.min(
			(naturalWidth / renderedWidth) * 100,
			(naturalHeight / renderedHeight) * 100
		)
	);

	return Math.max(100, naturalSizeZoomPercent);
}

function getZoomPoint(container: HTMLElement): { x: number; y: number } {
	const containerRect = container.getBoundingClientRect();
	const viewerImage = container.querySelector<HTMLElement>('.iv-image');
	const imageRect = viewerImage?.getBoundingClientRect() ?? containerRect;
	const percentageX = clampPercentage(props.zoomPosition.x) / 100;
	const percentageY = clampPercentage(props.zoomPosition.y) / 100;

	return {
		x: imageRect.left - containerRect.left + imageRect.width * percentageX,
		y: imageRect.top - containerRect.top + imageRect.height * percentageY
	};
}

function handleViewerImageLoaded({ container, instance }: ImageLoadedPayload) {
	viewer = instance;
	viewerContainer = container;
	container.classList.remove('zoomable-image-ready');
	container.querySelector<HTMLElement>('.iv-image')?.getBoundingClientRect();

	if (zoomFrame !== null) cancelAnimationFrame(zoomFrame);

	// iv-viewer resets to 100% immediately after notifying that the image loaded.
	// Defer the initial zoom so it is applied only after the complete image has
	// been measured and placed inside the container.
	zoomFrame = requestAnimationFrame(() => {
		zoomFrame = null;

		if (isUnmounted || viewer !== instance) return;

		container.classList.add('zoomable-image-ready');

		if (maximumZoomPercent > 100) {
			instance.zoom(maximumZoomPercent, getZoomPoint(container));
		}
	});
}

function updateImagePosition() {
	panFrame = null;

	const container = viewerContainer;
	const pointerPosition = pendingPointerPosition;
	const viewerImage = container?.querySelector<HTMLElement>('.iv-image');

	if (!container || !pointerPosition || !viewerImage) return;

	const containerRect = container.getBoundingClientRect();
	const imageRect = viewerImage.getBoundingClientRect();
	const percentageX = clampPercentage(
		((pointerPosition.x - containerRect.left) / containerRect.width) * 100
	);
	const percentageY = clampPercentage(
		((pointerPosition.y - containerRect.top) / containerRect.height) * 100
	);
	const horizontalOverflow = Math.max(0, imageRect.width - containerRect.width);
	const verticalOverflow = Math.max(0, imageRect.height - containerRect.height);

	viewerImage.style.left =
		horizontalOverflow > 0
			? `${-horizontalOverflow * (percentageX / 100)}px`
			: `${(containerRect.width - imageRect.width) / 2}px`;
	viewerImage.style.top =
		verticalOverflow > 0
			? `${-verticalOverflow * (percentageY / 100)}px`
			: `${(containerRect.height - imageRect.height) / 2}px`;
}

function handleMouseMove(event: MouseEvent) {
	if (!isDesktopViewport() || !viewer || maximumZoomPercent <= 100) return;

	pendingPointerPosition = { x: event.clientX, y: event.clientY };

	if (panFrame === null) {
		panFrame = requestAnimationFrame(updateImagePosition);
	}
}

function handleZoomClick(event: MouseEvent) {
	const target = event.target;

	if (!(target instanceof Element)) return;

	if (target.classList.contains('iv-image')) {
		closeZoom();
	}
}

function handleZoomKeydown(event: KeyboardEvent) {
	if (![' ', 'Enter', 'Escape'].includes(event.key)) return;

	event.preventDefault();
	event.stopPropagation();
	closeZoom();
}

function resetTouchGesture() {
	touchStartPosition = null;
	touchMoved = false;
}

function handleZoomTouchStart(event: TouchEvent) {
	if (event.touches.length !== 1) {
		resetTouchGesture();
		touchMoved = true;
		return;
	}

	const touch = event.touches[0];

	if (!touch) return;

	touchStartPosition = { x: touch.clientX, y: touch.clientY };
	touchMoved = false;
}

function handleZoomTouchMove(event: TouchEvent) {
	const startPosition = touchStartPosition;
	const touch = event.touches[0];

	if (!startPosition || event.touches.length !== 1 || !touch) {
		touchMoved = true;
		return;
	}

	const distanceX = Math.abs(touch.clientX - startPosition.x);
	const distanceY = Math.abs(touch.clientY - startPosition.y);

	if (distanceX > TAP_MOVE_TOLERANCE || distanceY > TAP_MOVE_TOLERANCE) {
		touchMoved = true;
	}
}

function handleZoomTouchEnd(event: TouchEvent) {
	const target = event.target;
	const isSingleTap =
		touchStartPosition !== null &&
		!touchMoved &&
		event.touches.length === 0 &&
		event.changedTouches.length === 1;

	resetTouchGesture();

	if (
		isSingleTap &&
		target instanceof Element &&
		target.closest('.iv-container') !== null
	) {
		event.preventDefault();
		closeZoom();
	}
}

function closeZoom() {
	emits('close');
}

const onImageLoad = async () => {
	const image = imageRef.value;

	if (!image || viewer || isUnmounted) return;

	// Dynamic import - only loads when image is clicked for zoom
	const { default: ImageViewer } = await import('iv-viewer');

	if (isUnmounted || imageRef.value !== image) return;
	maximumZoomPercent = getMaximumZoomPercent(image);

	// @types/iv-viewer@2.0.3 omits the listener API available in iv-viewer@2.2.1.
	const ImageViewerWithListeners =
		ImageViewer as unknown as ImageViewerConstructor;

	viewer = new ImageViewerWithListeners(image, {
		maxZoom: maximumZoomPercent,
		zoomOnMouseWheel: true,
		snapView: false,
		listeners: {
			onImageLoaded: handleViewerImageLoaded
		}
	});
};

onMounted(() => {
	nextTick(() => zoomContainerRef.value?.focus({ preventScroll: true }));
});

onBeforeUnmount(() => {
	isUnmounted = true;

	if (zoomFrame !== null) cancelAnimationFrame(zoomFrame);
	if (panFrame !== null) cancelAnimationFrame(panFrame);

	viewer?.destroy();
	viewer = null;
	viewerContainer = null;
});
</script>
<!-- Styles moved to app/assets/css/main.css to avoid render-blocking CSS -->
